import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useBranding } from "../../branding";
import { PROBLEM_CODES, ROLE_LABELS, api, errorMessage, problemCode, useApiMutation, useApiQuery } from "../../api";
import { Button, EmptyState, RoleBadge, Skeleton } from "../../components";
import { homeForRole, useSession } from "../../session";
import { AsyncBoundary, ROUTES } from "../../shell";
import styles from "./Auth.module.css";
import { ChampMotDePasse } from "./ChampMotDePasse";
import { erreurConfirmation, erreurMotDePasse, jetonDuFragment } from "./MotDePasse";

/**
 * Acceptation d'une invitation : on montre qui l'on sera (adresse, rôle,
 * entreprise), on choisit un mot de passe, et la session s'ouvre.
 *
 * Le jeton vient du fragment de l'URL et repart dans le corps d'un POST : il
 * n'atteint jamais le serveur par l'adresse, donc jamais son journal.
 */
export function InvitationPage() {
  const { esn_name } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const { adopt } = useSession();
  const token = jetonDuFragment(location.hash);
  const apercu = useApiQuery(
    (o) => (token === null ? Promise.reject(new Error("jeton absent")) : api.lookupInvitation({ token }, o)),
    [token],
  );
  const [mdp, setMdp] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [touche, setTouche] = useState(false);
  const acceptation = useApiMutation((t: string, p: string) => api.acceptInvitation({ token: t, password: p }));

  if (token === null) {
    return (
      <EmptyState
        icon="🔗"
        title="Lien incomplet"
        description="Ce lien ne porte pas de jeton. Recopiez l'adresse complète du courriel d'invitation."
      />
    );
  }

  const code = problemCode(apercu.error) ?? problemCode(acceptation.error);
  if (code === PROBLEM_CODES.invitationExpired) {
    return (
      <EmptyState
        icon="⌛"
        title="Invitation expirée"
        description="Ce lien a dépassé sa durée de validité. Demandez à la personne qui vous a invité de renvoyer l'invitation."
      />
    );
  }
  if (code === PROBLEM_CODES.invitationUsed || code === PROBLEM_CODES.userNotInvited) {
    return (
      <EmptyState
        icon="✅"
        title="Invitation déjà acceptée"
        description="Votre compte est déjà actif : connectez-vous avec votre mot de passe."
        action={<Link to={ROUTES.connexion}>Se connecter</Link>}
      />
    );
  }
  if (code === PROBLEM_CODES.invitationNotFound) {
    return (
      <EmptyState
        icon="🔗"
        title="Invitation inconnue"
        description="Ce lien ne correspond à aucune invitation. Il a peut-être été remplacé par un plus récent : ouvrez le dernier courriel reçu."
      />
    );
  }

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    setTouche(true);
    if (erreurMotDePasse(mdp) !== null || erreurConfirmation(mdp, confirmation) !== null || token === null) {
      return;
    }
    const me = await acceptation.run(token, mdp);
    if (me === null) {
      return;
    }
    adopt(me);
    navigate(homeForRole(me.user.role), { replace: true });
  }

  return (
    <AsyncBoundary
      state={apercu}
      errorTitle="Invitation illisible"
      skeleton={
        <>
          <Skeleton variant="title" width="14rem" />
          <Skeleton lines={4} />
        </>
      }
    >
      {(inv) => (
        <>
          <div>
            <h1 className={styles.titre}>Bienvenue</h1>
            <p className={styles.intro}>
              Vous êtes invité(e) à rejoindre{" "}
              <strong>{inv.company_name ?? `l'équipe ${esn_name}`}</strong> comme{" "}
              {ROLE_LABELS[inv.role].toLowerCase()}. Choisissez votre mot de passe pour activer le compte.
            </p>
          </div>

          <form className={styles.formulaire} onSubmit={(e) => void soumettre(e)} noValidate>
            <dl className={styles.recap}>
              <dt>Adresse</dt>
              <dd>
                {/* Lecture seule mais présent : c'est ce qui permet au
                    gestionnaire de mots de passe d'associer l'identifiant. */}
                <input type="email" value={inv.email} readOnly autoComplete="username" aria-label="Adresse e-mail" />
              </dd>
              <dt>Rôle</dt>
              <dd>
                <RoleBadge role={inv.role} />
              </dd>
            </dl>

            {acceptation.error !== null && (
              <p className={styles.alerte} role="alert">
                {errorMessage(acceptation.error)}
              </p>
            )}

            <ChampMotDePasse
              label="Mot de passe"
              value={mdp}
              onChange={setMdp}
              autoComplete="new-password"
              showRules
              error={touche ? erreurMotDePasse(mdp) : null}
              disabled={acceptation.pending}
            />
            <ChampMotDePasse
              label="Confirmation"
              value={confirmation}
              onChange={setConfirmation}
              autoComplete="new-password"
              error={touche ? erreurConfirmation(mdp, confirmation) : null}
              disabled={acceptation.pending}
            />
            <Button type="submit" variant="primary" fullWidth loading={acceptation.pending}>
              Activer mon compte
            </Button>
          </form>
        </>
      )}
    </AsyncBoundary>
  );
}
